// Account view - user profile with friends and invitations tabs

import { css, html } from 'lit';
import { authState, logout } from '../../lib/auth.js';
import { BaseComponent } from '../../lib/base-component.js';

import './friends-view.js';
import './invitations-view.js';

/**
 * Account page showing user info, tabbed friends/invitations, and logout
 */
class AccountView extends BaseComponent {
  static properties = {
    _activeTab: { type: String, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }

    .profile-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 1.5rem;
    }

    .profile-info {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    .avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      object-fit: cover;
    }

    .avatar-placeholder {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background-color: #6c757d;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.1rem;
      font-weight: 600;
      color: white;
    }

    .username {
      font-size: 1.1rem;
      font-weight: 600;
      margin: 0;
    }

    .email {
      color: #6c757d;
      margin: 0;
      font-size: 0.85rem;
    }

    .nav-tabs {
      display: flex;
      border-bottom: 2px solid #dee2e6;
      margin-bottom: 1rem;
      gap: 0;
    }

    .nav-tab {
      padding: 0.5rem 1rem;
      border: none;
      background: none;
      cursor: pointer;
      font-weight: 500;
      color: #6c757d;
      border-bottom: 2px solid transparent;
      margin-bottom: -2px;
      transition:
        color 0.15s,
        border-color 0.15s;
    }

    .nav-tab:hover {
      color: #212529;
    }

    .nav-tab.active {
      color: #212529;
      border-bottom-color: #212529;
    }
  `;

  constructor() {
    super();
    const params = new URLSearchParams(window.location.search);
    this._activeTab = params.get('tab') === 'invitations' ? 'invitations' : 'friends';
  }

  get _user() {
    return authState.user;
  }

  get _username() {
    return this._user?.username || this._user?.email || 'User';
  }

  get _userInitial() {
    return this._username.charAt(0).toUpperCase();
  }

  _handleLogout() {
    logout();
  }

  render() {
    return html`
      <div class="profile-header">
        <div class="profile-info">
          ${
            this._user?.picture
              ? html` <img class="avatar" src="${this._user.picture}" alt="Avatar" /> `
              : html` <div class="avatar-placeholder">${this._userInitial}</div> `
          }
          <div>
            <h1 class="username">${this._username}</h1>
            ${this._user?.email ? html`<p class="email">${this._user.email}</p>` : ''}
          </div>
        </div>
        <button class="btn btn-outline-danger" @click="${this._handleLogout}">
          <i class="bi bi-box-arrow-right me-2"></i>Log Out
        </button>
      </div>

      <div class="nav-tabs">
        <button
          class="nav-tab ${this._activeTab === 'friends' ? 'active' : ''}"
          @click="${() => (this._activeTab = 'friends')}"
        >
          Friends
        </button>
        <button
          class="nav-tab ${this._activeTab === 'invitations' ? 'active' : ''}"
          @click="${() => (this._activeTab = 'invitations')}"
        >
          Game Invitations
        </button>
      </div>

      ${
        this._activeTab === 'friends'
          ? html`<friends-view></friends-view>`
          : html`<invitations-view></invitations-view>`
      }
    `;
  }
}

customElements.define('account-view', AccountView);
